'use client'

import { useState, useEffect } from 'react'
import { AnimatePresence } from 'motion/react'
import Image from 'next/image'
import Link from '@/components/ui/Link'
import AppBlock from '@/components/content/blocks/AppBlock'
import QuoteBlock from '@/components/content/blocks/QuoteBlock'
import ListBlock from '@/components/content/blocks/ListBlock'
import VideoPlayer from '@/components/content/videos/VideoPlayer'
import VideoSegmentChip from '@/components/content/video-segment/VideoSegmentChip'
import VideoAppChip from '@/components/content/video-segment/VideoAppChip'
import Toast from '@/components/ui/Toast'
import { Icon } from '@/icons/Icon'
import { lightbox } from '@/lib/lightbox'
import { createLogger } from '@/lib/logger'
import { cn, isPopulatedObject, toSlug, scrollToSection } from '@/lib/utils'
import {
  classifySafeRichTextUrl,
  isEfficientReferralUrl,
  type SafeRichTextUrlKind,
} from '@/lib/rich-text-link-url'
import type { Block } from '@/types/app'
import type { App, Video } from '@/payload-types'
import type { ResolvedVideoApp, ResolvedVideoSegment } from '@/lib/lexical-json/types'

const logger = createLogger('RichText')

type RichTextProps = {
  id?: string | null
  content: Block[]
  readMore?: string | null
  theme?: 'light' | 'dark'
  truncate?: boolean
  maxLength?: number
  indent?: boolean
  offset?: number
}

type TextLinkProps = {
  child: any
  id?: string | number
  type?: string
}

type TableCellProps = {
  cell: Block
  cellIndex: number
  isLastCell: boolean
  isLastRow: boolean
}

function TableCellContent({ children }: { children: Block['children'] }) {
  return children?.map((child, index) => {
    if (child.type === 'paragraph') {
      return (
        <p
          key={`table-paragraph-${index}`}
          style={{ textAlign: child.format as 'left' | 'center' | 'right' }}
        >
          {child.children?.map((paragraphChild, childIndex) => (
            <TextLink
              key={`table-paragraph-content-${childIndex}`}
              id={`table-paragraph-content-${childIndex}`}
              child={paragraphChild}
              type={child.type}
            />
          ))}
        </p>
      )
    }

    if (child.type === 'list') {
      const Tag = child.tag === 'ol' ? 'ol' : 'ul'
      return (
        <Tag
          key={`table-list-${index}`}
          className={cn('ml-4 space-y-1', child.tag === 'ol' ? 'list-decimal' : 'list-disc')}
        >
          {child.children?.map((item, itemIndex) => (
            <li key={`table-list-item-${itemIndex}`}>
              <TableCellContent children={'children' in item ? item.children : []} />
            </li>
          ))}
        </Tag>
      )
    }

    if (child.type === 'heading') {
      return (
        <p key={`table-heading-${index}`} className="font-semibold text-gray-950 dark:text-white">
          <TableCellContent children={child.children} />
        </p>
      )
    }

    if (child.type === 'text' || child.type === 'link' || child.type === 'linebreak') {
      return <TextLink key={`table-inline-${index}`} id={`table-inline-${index}`} child={child} />
    }

    return 'children' in child && child.children?.length ? (
      <TableCellContent key={`table-content-${index}`} children={child.children} />
    ) : null
  })
}

const NUMERIC_CELL_PATTERN = /^[+-]?[$\u20AC\u00A3]?\d[\d,]*(\.\d+)?%?$/

function getCellColSpan(cell: Block): number {
  return typeof cell.colSpan === 'number' && cell.colSpan > 1 ? cell.colSpan : 1
}

function getCellPlainText(children: Block['children']): string {
  return (children ?? [])
    .map((child) => {
      if (typeof child === 'string') return child
      if ('text' in child && typeof child.text === 'string') return child.text
      if ('children' in child && child.children) return getCellPlainText(child.children)
      return ''
    })
    .join('')
    .trim()
}

function RichTextTableCell({ cell, cellIndex, isLastCell, isLastRow }: TableCellProps) {
  const headerState = typeof cell.headerState === 'number' ? cell.headerState : 0
  const Tag = headerState > 0 ? 'th' : 'td'
  const scope = headerState === 1 ? 'col' : headerState === 2 ? 'row' : undefined
  const colSpan = getCellColSpan(cell) > 1 ? getCellColSpan(cell) : undefined
  const rowSpan = typeof cell.rowSpan === 'number' && cell.rowSpan > 1 ? cell.rowSpan : undefined
  const backgroundColor =
    typeof cell.backgroundColor === 'string' && cell.backgroundColor
      ? cell.backgroundColor
      : undefined
  const verticalAlign =
    cell.verticalAlign === 'middle' || cell.verticalAlign === 'bottom' ? cell.verticalAlign : 'top'
  const isNumeric = headerState === 0 && NUMERIC_CELL_PATTERN.test(getCellPlainText(cell.children))

  return (
    <Tag
      key={`table-cell-${cellIndex}`}
      scope={scope}
      colSpan={colSpan}
      rowSpan={rowSpan}
      className={cn(
        'whitespace-normal break-words border-b border-r border-gray-200 px-4 py-3 text-left text-14 leading-relaxed first:min-w-48 first:max-w-[28rem] last:border-r-0 dark:border-white/15',
        headerState > 0
          ? 'bg-zinc-100 font-semibold text-gray-950 dark:bg-white/10 dark:text-white'
          : 'text-gray-700 dark:text-white/70',
        isNumeric && 'text-right [font-variant-numeric:tabular-nums]',
      )}
      style={{
        backgroundColor,
        borderBottom: isLastRow ? '0' : '1px solid rgba(148, 163, 184, 0.3)',
        borderRight: isLastCell ? '0' : '1px solid rgba(148, 163, 184, 0.3)',
        fontSize: '0.875rem',
        fontWeight: headerState > 0 ? 600 : undefined,
        lineHeight: 1.5,
        padding: '0.75rem 1rem',
        textAlign: isNumeric ? 'right' : 'left',
        verticalAlign,
      }}
    >
      <div className="space-y-2">
        <TableCellContent children={cell.children} />
      </div>
    </Tag>
  )
}

function RichTextTable({ block }: { block: Block }) {
  const rows = block.children?.filter((row): row is Block => row.type === 'tablerow') ?? []
  const rowStriping = block.rowStriping === true

  return (
    <div
      className="w-full overflow-x-auto overscroll-x-contain rounded-xl border border-gray-200 bg-white shadow-sm dark:border-white/15 dark:bg-white/[0.03]"
      style={{
        border: '1px solid rgba(148, 163, 184, 0.3)',
        borderRadius: '0.75rem',
        overflowX: 'auto',
        overscrollBehaviorX: 'contain',
        width: '100%',
      }}
    >
      <table
        className="w-full border-separate border-spacing-0 text-left"
        style={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          tableLayout: 'auto',
          textAlign: 'left',
          width: '100%',
        }}
      >
        <tbody>
          {rows.map((row, rowIndex) => {
            const cells =
              row.children?.filter((cell): cell is Block => cell.type === 'tablecell') ?? []
            const isStripedRow = rowStriping && rowIndex % 2 === 1

            return (
              <tr
                key={`table-row-${rowIndex}`}
                className={cn(
                  'transition-colors hover:bg-zinc-50 dark:hover:bg-white/5 last:[&>td]:border-b-0 last:[&>th]:border-b-0',
                  isStripedRow && 'bg-zinc-50 dark:bg-white/5',
                )}
              >
                {cells.map((cell, cellIndex) => (
                  <RichTextTableCell
                    key={`table-cell-${cellIndex}`}
                    cell={cell}
                    cellIndex={cellIndex}
                    isLastCell={cellIndex === cells.length - 1}
                    isLastRow={rowIndex === rows.length - 1}
                  />
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function truncateBlocks(blocks: Block[], maxLength: number): Block[] {
  let totalLength = 0
  const truncatedBlocks: Block[] = []

  for (const block of blocks) {
    if (block.children) {
      const text = block.children
        .map((child) => (typeof child === 'string' ? child : child.text || ''))
        .join('')
      const remainingLength = maxLength - totalLength

      if (totalLength >= maxLength) break

      if (totalLength + text.length <= maxLength) {
        truncatedBlocks.push(block)
        totalLength += text.length
      } else {
        // Truncate the last block
        const truncatedText = text.slice(0, remainingLength) + '...'
        truncatedBlocks.push({
          ...block,
          children: [{ text: truncatedText, type: 'text', format: 0 }],
        })
        break
      }
    } else {
      truncatedBlocks.push(block)
    }
  }

  return truncatedBlocks
}

export default function RichText({
  id,
  content,
  readMore,
  theme,
  truncate = false,
  maxLength = 300,
  indent = false,
  offset = 130,
}: RichTextProps) {
  const [copied, setCopied] = useState<boolean>(false)
  const processedContent = truncate ? truncateBlocks(content, maxLength) : content

  const copyLink = (id: string) => {
    const currentUrl = `${window.location.origin}${window.location.pathname}`
    navigator.clipboard.writeText(`${currentUrl}#${id}`)
    setCopied(true)

    setTimeout(() => {
      setCopied(false)
    }, 2000)
  }

  useEffect(() => {
    lightbox.init()

    // Named handler so we can remove it in cleanup. Without removal, every
    // effect re-run (on `processedContent` change) accumulated another
    // window-level wheel listener, and listeners survived unmount.
    const closeOnWheel = () => {
      lightbox.pswp?.close()
    }
    window.addEventListener('wheel', closeOnWheel)

    lightbox.on('openingAnimationEnd', () => {
      document.body.style.overflow = 'hidden'
    })

    lightbox.on('closingAnimationEnd', () => {
      document.body.style.overflow = ''
    })

    return () => {
      window.removeEventListener('wheel', closeOnWheel)
      lightbox.destroy()
    }
  }, [processedContent])

  if (!content) {
    return null
  }

  const ImageBlock = (id: string, value: any) => {
    const imageUrl = (value?.url as string) ?? ''
    if (!imageUrl) return null

    const imageAlt = value?.alt as string
    const imageCaption = value?.caption as any
    const imageWidth = value?.width
    const imageHeight = value?.height

    return (
      <div key={`img-${id}`} className="mt-2 flex flex-col items-end gap-1">
        <Link
          href={imageUrl}
          data-pswp-width={imageWidth}
          data-pswp-height={imageHeight}
          className="lightbox-image cursor-zoom-in"
          target="_blank"
        >
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={800}
            height={800}
            className={cn(
              'rounded-lg border object-contain',
              theme === 'dark' ? 'border-zinc-800' : 'border-dark-100 dark:border-white/20',
            )}
          />
        </Link>
        {imageCaption ? (
          <div className="text-12 text-dark-400">
            <RichText content={imageCaption.root.children} />
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-5" id="rich-content">
        {processedContent.map((block, index) => {
          switch (block.type) {
            case 'heading': {
              const HeaderBlock = ({
                tag,
                slug,
                content,
                index,
                copy = false,
              }: {
                tag: string
                slug?: string
                content: string
                index: number
                copy?: boolean
              }) => {
                const Tag = tag as 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
                const includeSectionId = tag === 'h2' || tag === 'h3' || tag === 'h4'

                return (
                  <div
                    key={`${tag}-${index}`}
                    id={slug}
                    data-section-id={includeSectionId ? slug : undefined}
                    data-name={content}
                    onClick={() => {
                      if (slug && copy) {
                        copyLink(slug)
                        scrollToSection({ id: slug, offset, animate: true })
                      }
                    }}
                    className="-mb-3"
                  >
                    <Tag className={cn(indent ? 'px-1' : '', copy ? 'underlined group' : '')}>
                      {content}

                      {copy && (
                        <Icon
                          name="hyperlink"
                          size={12}
                          className="absolute right-0 top-1/2 h-5 w-5 origin-center -translate-y-1.5 translate-x-4 scale-0 opacity-0 transition-all duration-500 ease-custom group-hover:translate-x-5 group-hover:scale-100 group-hover:opacity-100"
                        />
                      )}
                    </Tag>
                  </div>
                )
              }

              return (
                <HeaderBlock
                  key={`h-${index}`}
                  tag={(block.tag as string) || 'h4'}
                  slug={block.slug as string}
                  content={
                    block.children?.length
                      ? block.children
                          .map((child) =>
                            typeof child === 'string'
                              ? child
                              : (child.text as string | undefined) || '',
                          )
                          .join('')
                      : (block.content as string)
                  }
                  index={index}
                />
              )
            }
            case 'paragraph':
              if (block.children?.[0]?.type === 'upload') {
                return ImageBlock(block.children[0].id as string, block.children[0].value)
              } else {
                return (
                  <p
                    style={{
                      textAlign: block.format as 'left' | 'center' | 'right',
                      fontWeight: block.textFormat === 1 ? '500' : '400',
                    }}
                    key={`p-${index}`}
                    className={cn(indent ? 'px-1' : '')}
                  >
                    {block.children.map((child, childIndex) => (
                      <TextLink
                        key={`p-content-${childIndex}`}
                        id={`p-content-${childIndex}`}
                        child={child}
                        type={block.type}
                      />
                    ))}
                    {readMore && index === content.length - 1 && (
                      <Link
                        href={readMore}
                        theme="primary"
                        className="underlined primary ml-1 inline-flex"
                      >
                        Learn more
                      </Link>
                    )}
                  </p>
                )
              }
            case 'upload':
              return ImageBlock(block.id as string, block.value)
            case 'list': {
              const Tag = block.tag === 'ol' ? 'ol' : 'ul'
              return (
                <Tag
                  key={`list-${index}`}
                  className={cn(
                    'list-outside space-y-3 pl-4.5 pr-base md:pl-5',
                    'marker:text-black/70 dark:marker:text-white/70',
                    block.tag === 'ul' ? 'list-disc' : 'list-decimal',
                  )}
                >
                  {block.children.map((item, itemIndex) => {
                    if (item.type === 'listitem') {
                      const hasOnlyNestedList =
                        item.children?.length === 1 && item.children[0]?.type === 'list'

                      if (hasOnlyNestedList) {
                        const nestedList = item.children[0] as Block
                        const NestedTag = nestedList.tag === 'ol' ? 'ol' : 'ul'
                        return (
                          <li key={`unwrapped-list-${itemIndex}`} className="list-none">
                            <NestedTag
                              className={cn(
                                'mt-2 list-outside space-y-3 pl-3.5 pr-base',
                                'marker:text-black/70 dark:marker:text-white/70',
                                nestedList.tag === 'ul' ? 'list-disc' : 'list-decimal',
                              )}
                            >
                              {nestedList.children.map((nestedItem, nestedIndex) => {
                                if (nestedItem.type === 'listitem') {
                                  return (
                                    <li
                                      key={`unwrapped-list-item-${nestedIndex}`}
                                      style={{
                                        textAlign: nestedItem.format as 'left' | 'center' | 'right',
                                      }}
                                    >
                                      {nestedItem.children?.map((nestedChild, nestedChildIndex) => (
                                        <TextLink
                                          key={`unwrapped-list-content-${nestedChildIndex}`}
                                          id={`unwrapped-list-content-${nestedChildIndex}`}
                                          child={nestedChild}
                                          type={nestedList.type}
                                        />
                                      ))}
                                    </li>
                                  )
                                }
                                return null
                              })}
                            </NestedTag>
                          </li>
                        )
                      }

                      return (
                        <li
                          key={`list-item-${itemIndex}`}
                          style={{ textAlign: item.format as 'left' | 'center' | 'right' }}
                        >
                          {item.children.map((child, childIndex) => {
                            if (child.type === 'list') {
                              const NestedTag = child.tag === 'ol' ? 'ol' : 'ul'
                              return (
                                <NestedTag
                                  key={`nested-list-${childIndex}`}
                                  className={cn(
                                    'mt-2 list-outside space-y-3 pl-3.5 pr-base',
                                    'marker:text-black/70 dark:marker:text-white/70',
                                    child.tag === 'ul' ? 'list-disc' : 'list-decimal',
                                  )}
                                >
                                  {child.children.map((nestedItem, nestedIndex) => {
                                    if (nestedItem.type === 'listitem') {
                                      return (
                                        <li
                                          key={`nested-list-item-${nestedIndex}`}
                                          style={{
                                            textAlign: nestedItem.format as
                                              | 'left'
                                              | 'center'
                                              | 'right',
                                          }}
                                          className="pl-3"
                                        >
                                          {nestedItem.children?.map(
                                            (nestedChild, nestedChildIndex) => (
                                              <TextLink
                                                key={`nested-list-content-${nestedChildIndex}`}
                                                id={`nested-list-content-${nestedChildIndex}`}
                                                child={nestedChild}
                                                type={child.type}
                                              />
                                            ),
                                          )}
                                        </li>
                                      )
                                    }
                                    return null
                                  })}
                                </NestedTag>
                              )
                            }
                            return (
                              <TextLink
                                key={`list-content-${childIndex}`}
                                id={`list-content-${childIndex}`}
                                child={child}
                                type={block.type}
                              />
                            )
                          })}
                        </li>
                      )
                    }
                    return null
                  })}
                </Tag>
              )
            }
            case 'quote':
              return (
                <blockquote
                  key={`quote-${index}`}
                  className="mx-auto flex w-full gap-3.5 rounded-lg bg-blue-50 pb-4 pl-4 pr-10 pt-3.5 text-blue-900 dark:bg-blue-400/15 dark:text-blue-200"
                >
                  <div className="flex h-7 shrink-0 items-center">
                    <Icon name="circle-info" />
                  </div>
                  <div>
                    {block.children.map((child, childIndex) => (
                      <TextLink
                        key={`quote-text-${childIndex}`}
                        id={`quote-text-${childIndex}`}
                        child={child}
                        type={block.type}
                      />
                    ))}
                  </div>
                </blockquote>
              )
            case 'horizontalrule':
              return <hr key={`hr-${index}`} className="mx-auto my-3 h-px w-full bg-dark-100"></hr>
            case 'table':
              return <RichTextTable key={`table-${index}`} block={block} />
            case 'block': {
              if (!block.fields) return null

              switch (block.fields.blockType) {
                case 'appTemplate': {
                  const appField = block.fields.app
                  if (!appField || typeof appField === 'number') return null

                  const app = appField as App
                  const appSlug = app.slug ?? toSlug(app.name)
                  const sectionId = id ? `${id}-${appSlug}` : appSlug
                  return (
                    <AppBlock
                      key={`${block.blockType}-${index}`}
                      id={sectionId}
                      body={block.fields.body}
                      app={app}
                    />
                  )
                }
                case 'quoteTemplate':
                  return (
                    <QuoteBlock
                      key={`${block.blockType}-${index}`}
                      quote={block.fields.quote as any}
                      authors={block.fields.authors as any}
                      editor={true}
                    />
                  )
                case 'listTemplate':
                  return (
                    <ListBlock
                      key={`${block.blockType}-${index}`}
                      lists={block.fields.lists as any}
                      editor={true}
                    />
                  )
                default:
                  return <h2 key={index}>Block not found</h2>
              }
            }
            case 'relationship': {
              if (block.relationTo === 'video') {
                const video = block.value as Video | Video['id'] | null | undefined
                if (!isPopulatedObject<Video>(video) || !video.name) {
                  logger.warn(
                    'Video relationship node is not populated. Ensure the fetcher includes video in its populate map.',
                    { videoValue: video },
                  )
                  return null
                }
                return (
                  <div key={`video-${index}`} className="mt-2">
                    <VideoPlayer video={video} />
                  </div>
                )
              }

              if (block.relationTo === 'video_segment') {
                // videoSegment payload is injected on the block-level relationship
                // node by enrichVideoSegmentLinks (see transform.ts). Same rendering
                // path as inline link-shaped segments.
                const videoSegment = (block as { videoSegment?: ResolvedVideoSegment }).videoSegment
                if (!videoSegment) {
                  logger.warn(
                    'video_segment relationship node missing enriched payload — was it loaded via afterRead transform?',
                    { value: block.value },
                  )
                  return null
                }
                return (
                  <div key={`video-segment-${index}`}>
                    <VideoSegmentChip segment={videoSegment} />
                  </div>
                )
              }

              if (block.relationTo === 'video_apps_app') {
                // Parallel to video_segment: enricher (enrichVideoAppLinks)
                // injects the payload at afterRead. If missing, skip render.
                const videoApp = (block as { videoApp?: ResolvedVideoApp }).videoApp
                if (!videoApp) {
                  logger.warn(
                    'video_apps_app relationship node missing enriched payload — was it loaded via afterRead transform?',
                    { value: block.value },
                  )
                  return null
                }
                return (
                  <div key={`video-app-${index}`} className="flex justify-center">
                    <VideoAppChip videoApp={videoApp} />
                  </div>
                )
              }

              return null
            }
            default:
              return null
          }
        })}
      </div>
      <AnimatePresence>{copied && <Toast>Link copied to clipboard</Toast>}</AnimatePresence>
    </>
  )
}

// Only applies to internal (NextLink) hrefs: Link.tsx recomputes rel itself for absolute
// and mailto hrefs, so affiliate/external rel is unaffected by what we pass here.
// nofollow is for outbound links; our own internal paths must stay followable.
function getNewTabRel(
  kind: SafeRichTextUrlKind | null,
  newTab: boolean,
): 'noopener' | 'noopener nofollow' | undefined {
  if (!newTab) return undefined
  if (kind === 'internal') return 'noopener'
  return 'noopener nofollow'
}

export const TextLink = ({ child, id, type }: TextLinkProps) => {
  switch (child.type) {
    case 'link': {
      // video_segment links render as an interactive chip that opens a shared
      // popout player. videoSegment payload is injected server-side by
      // enrichVideoSegmentLinks in transform.ts (at the top-level node, not
      // inside .fields). If enrichment failed (segment deleted, etc.), fall
      // through to standard Link rendering.
      const videoSegment = child.videoSegment
      const isSegmentLink =
        child.fields?.linkType === 'internal' && child.fields?.doc?.relationTo === 'video_segment'
      if (isSegmentLink && videoSegment) {
        const linkText = child.children?.map((linkChild: any) => linkChild.text).join('') || ''
        return <VideoSegmentChip key={id} segment={videoSegment} label={linkText || undefined} />
      }

      // Parallel inline-link handling for video_apps_app refs.
      const videoApp = child.videoApp as ResolvedVideoApp | undefined
      const isVideoAppLink =
        child.fields?.linkType === 'internal' && child.fields?.doc?.relationTo === 'video_apps_app'
      if (isVideoAppLink && videoApp) {
        const linkText = child.children?.map((linkChild: any) => linkChild.text).join('') || ''
        return <VideoAppChip key={id} videoApp={videoApp} label={linkText || undefined} />
      }

      // Internal links are transformed server-side via afterRead hook (transform.ts):
      // - linkType='internal' with doc.relationTo='link' resolves to the affiliate shortLink URL
      //   and gets newTab=true + passReferrerUrl injected onto child.fields.
      // - Other internal collections resolve to their slugified path.
      // - linkType='custom' stays as-is with the raw url.
      // Link.tsx only applies `rel="...sponsored"` when the caller passes `sponsored`. We treat
      // every editorial efficient.link/r link as sponsored: we only embed an affiliate link in
      // rich text when there's a partner relationship, so partnership tier doesn't need resolving here.
      const linkText = child.children?.map((linkChild: any) => linkChild.text).join('') || ''
      const href = child.fields?.url
      const renderedUrlKind = classifySafeRichTextUrl(href)
      const newTab = renderedUrlKind !== null && child.fields?.newTab === true
      const rel = getNewTabRel(renderedUrlKind, newTab)
      const isReferralLink = isEfficientReferralUrl(href)
      const passReferrerUrl =
        typeof child.fields?.passReferrerUrl === 'boolean'
          ? child.fields.passReferrerUrl
          : undefined

      const bold = ((child.children?.[0]?.format ?? 0) & 1) === 1
      const italic = ((child.children?.[0]?.format ?? 0) & 2) === 2

      if (!renderedUrlKind) {
        return (
          <span
            key={id}
            style={{
              fontWeight: bold ? 'bold' : 'normal',
              fontStyle: italic ? 'italic' : 'normal',
            }}
          >
            {linkText}
          </span>
        )
      }

      return (
        <Link
          href={href}
          target={newTab ? '_blank' : undefined}
          rel={rel}
          passReferrerUrl={passReferrerUrl}
          sponsored={isReferralLink}
          key={id}
          className="inline underline decoration-1 underline-offset-3 transition-colors hover:text-primary-500"
          style={{
            fontWeight: bold ? 'bold' : 'normal',
            fontStyle: italic ? 'italic' : 'normal',
          }}
        >
          {linkText}
        </Link>
      )
    }
    case 'text': {
      const bold = child.format === 1 || type === 'quote'
      const italic = child.format === 2
      const code = (child.format & 16) !== 0

      if (code) {
        return (
          <code
            key={id}
            className="rounded-key border border-gray-200 bg-gray-100 px-1.5 py-0.5 font-mono text-[0.9em] text-gray-800 dark:border-white/20 dark:bg-white/10 dark:text-white/60"
          >
            {child.text as string}
          </code>
        )
      }

      return (
        <span
          key={id}
          style={{
            fontWeight: bold ? '600' : undefined,
            fontStyle: italic ? 'italic' : 'normal',
          }}
        >
          {child.text as string}
        </span>
      )
    }
    case 'linebreak':
      return <br key={id} />
  }

  return null
}
